import { createServerFn } from "@tanstack/react-start"
import { requireAuth, requireScopes, rateLimitFunction, createPostHogTrackingMiddleware } from "~/lib/middleware"
import z from "zod"
import { LogtoScopes } from "~/lib/logto/logto"
import { LogtoManagementApi } from "~/lib/logto/logto-management-api"
import { deleteGroup } from "~/lib/abodemail/generated/api"

export const deleteOrganizationSchema = z.object({
  organization_id: z.string().min(1, 'Organization ID is required'),
})

export type DeleteOrganizationRequest = z.infer<typeof deleteOrganizationSchema>

export const deleteOrganization = createServerFn({
  method: 'POST',
})
  .middleware([requireAuth, rateLimitFunction, requireScopes(LogtoScopes.AdminOrgCrud), createPostHogTrackingMiddleware('delete_organization')])
  .validator(deleteOrganizationSchema)
  .handler(async (ctx) => {
    const { organization_id } = ctx.data
    
    // Use real API
    const API_MANAGEMENT_BASE_URL = process.env.API_MANAGEMENT_BASE_URL || 'http://localhost:8000'
    const BFF_API_KEY = process.env.API_MANAGEMENT_API_KEY
    
    if (!BFF_API_KEY) {
      throw new Error('API_MANAGEMENT_API_KEY environment variable is required')
    }
    
    const params = new URLSearchParams({ organization_id })
    
    const response = await fetch(`${API_MANAGEMENT_BASE_URL}/api/v4/admin/organizations/delete?${params}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${BFF_API_KEY}`,
        'Content-Type': 'application/json',
      },
    })
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }))
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`)
    }

    //delete from logto if logto is setup
    if (process.env.LOGTO_MANAGEMENT_ENDPOINT) {
      await LogtoManagementApi.deleteOrganization(organization_id)
    }
    
    // Delete corresponding group from AbodeMail
    try {
      await deleteGroup(organization_id)
      console.log(`Deleted AbodeMail group for organization: ${organization_id}`)
    } catch (abodeMailError) {
      console.error('Failed to delete AbodeMail group:', abodeMailError)
      // Don't fail the organization deletion if AbodeMail group deletion fails
    }
    
    return { success: true, message: 'Organization deleted successfully' }
  }) 
